import { ActionKind, Icon } from '@dynamic/ui';
import { FC } from 'react';

import { MenuItem } from '#/client/models';
import { useConfig } from '#/hooks/useConfig';
import { MenuFocusStyle } from '#/service/constants';
import { InternalPageId, MenuItemDisplayType } from '#/service/constants/menu';
import { techSearchParams } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { MenuButton, MenuItemBoxBg, MenuLink, MenuNavLink } from './components';
import { getNavigationTarget, getNavigationTargetId } from './Menu.utils';
import { SettingsMenuLink } from './SettingsLink';

// menu item target -> title dictionary key
const itemTargetLabels: Partial<Record<InternalPageId, string>> = {
  [InternalPageId.Search]: 'button.search',
  [InternalPageId.Login]: 'button.login',
};

interface ItemLinkProps {
  item: MenuItem;
  defaultSelectedItem: string;
  showIcons?: boolean;
  onClick?: () => void;
  inHamburger?: boolean;
}

export const ItemLink: FC<ItemLinkProps> = ({
  item,
  defaultSelectedItem,
  showIcons,
  onClick,
  inHamburger,
}) => {
  const {
    theme: {
      menu: { focus: focusStyle },
    },
    pages,
  } = useConfig();

  const {
    display: {
      icon = null,
      type: displayType = '',
      title = '',
      buttonVariant = undefined,
    } = {},
    action,
  } = item ?? {};

  const hasIcon =
    !!icon &&
    (MenuItemDisplayType.Icon === displayType ||
      MenuItemDisplayType.IconText === displayType ||
      showIcons);

  const isMore = action?.parameters?.view === InternalPageId.More;

  const hasText =
    MenuItemDisplayType.Text === displayType ||
    MenuItemDisplayType.IconText === displayType ||
    MenuItemDisplayType.Button === displayType;

  const children = (
    <>
      {(hasIcon || hasText) && (
        <MenuItemBoxBg $iconOnly={hasIcon && !hasText} className="box">
          {hasIcon && <Icon className="menuItem" key="icon" name={icon} />}
          {hasText && (
            <span
              className="title menuItem"
              data-testid="menuItemTitle"
              key="title"
            >
              {tb(title)}
            </span>
          )}
        </MenuItemBoxBg>
      )}
    </>
  );

  const ariaLabel =
    item.display.title ??
    tb(itemTargetLabels[item.action?.parameters?.view as InternalPageId]);

  const search = techSearchParams(document.location.search);

  const commonProps = {
    key: getNavigationTargetId(item),
    'aria-label': ariaLabel,
    $buttonVariant: buttonVariant,
    $focusStyle: buttonVariant ? MenuFocusStyle.None : focusStyle,
    onClick,
    $inHamburger: inHamburger,
    children,
  };

  if (isMore) {
    const { children, ...menuButtonProps } = commonProps;

    return (
      <SettingsMenuLink menuButtonProps={menuButtonProps}>
        {children}
      </SettingsMenuLink>
    );
  }

  // Login page will not be a route but will prompt a popup
  if (action?.parameters?.view === InternalPageId.Login) {
    return <MenuButton type="button" {...commonProps} />;
  }

  const { internalUrl, externalUrl } = getNavigationTarget(
    item,
    defaultSelectedItem,
    pages
  );

  if (action?.kind === ActionKind.NavigateExternal) {
    return <MenuLink {...commonProps} href={externalUrl} target="_blank" />;
  }

  return (
    <MenuNavLink
      {...commonProps}
      to={{ pathname: internalUrl, search: search }}
    />
  );
};
